import { Component, OnInit, TemplateRef, inject, ChangeDetectionStrategy, viewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { RegistrationService } from '../../services/registration-service.service';
import { CitadelAutoCompleteComponent } from '@citadel/controls';
import { BehaviorSubject } from 'rxjs';
import { SignInService } from '../../services/sign-in-service';
import { CitadelNotificationService } from '@citadel/core';
import { RecaptchaService } from 'src/app/services/recaptcha.service';
import { CountryModel } from 'src/app/models/country.model';

@Component({
    selector: 'app-forgot-password-page',
    templateUrl: './forgot-password-page.component.html',
    styleUrls: ['./forgot-password-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ForgotPasswordPageComponent implements OnInit {
  private fb = inject(FormBuilder);
  private http = inject(HttpClient);
  private router = inject(Router);
  private registration = inject(RegistrationService);
  private recaptchaService = inject(RecaptchaService);
  private formBuilder = inject(FormBuilder);
  private signInService = inject(SignInService);
  private notify = inject(CitadelNotificationService);
  private route = inject(ActivatedRoute);

  readonly otpchannel = viewChild<TemplateRef<any>>('otpchannel');
  readonly enterotp = viewChild<TemplateRef<any>>('enterotp');
  readonly password = viewChild<TemplateRef<any>>('password');
  readonly completeregistration = viewChild<TemplateRef<any>>('completeregistration');
  readonly country = viewChild<CitadelAutoCompleteComponent>('country');
  readonly userEntryTemplate = viewChild<TemplateRef<any>>('userEntryTemplate');
  readonly emailSentTemplate = viewChild<TemplateRef<any>>('emailSentTemplate');
  readonly resetTemplate = viewChild<TemplateRef<any>>('resetTemplate');
  readonly otpTemplate = viewChild<TemplateRef<any>>('otpTemplate');

  selectedIndex = 0;
  steps: any = [
    { title: new BehaviorSubject<string>('New password') },
    { title: new BehaviorSubject<string>('One-time-PIN') }
  ];
  private editedPasswords = { 
    // CitadelInput doesn't update the form on input, but rather on change, so we need to keep track of the input value
    password: '', 
    confirmPassword: '' 
  };
  // Set only from the viewChild signals below, each of which is `TemplateRef<any> | undefined`
  // until its template exists in the DOM.
  currentTemplate?: TemplateRef<any>;
  // Set synchronously in ngOnInit (setPasswordForm) before Angular's first change-detection pass
  // can read it.
  passwordForm!: FormGroup;
  // Dead field: declared, never assigned or read anywhere in this component.
  identityForm?: FormGroup;
  // Never populated or read in this component (unlike its sibling registration-page, which fetches
  // and uses RegistrationService.getCountries()); typed by analogy for when it is.
  countries: CountryModel[] = [];
  // Left `any`: dead field - only a same-named local `const extras` in the constructor is ever used.
  extras: any;
  // Set from a query param or routingState in ngOnInit; if neither carries one, this stays unset
  // and the `.match()` call right after guards for that (see the bug note there).
  returnUrl?: string;
  // Carries SignInService.signInReturnUrl forward, which is itself unset until some other page
  // sets it; also assigned from a regex match (line ~126) which can itself be absent.
  redirectUrl: string | undefined;
  appID?: string;
  clientId?: string;
  registerMessage?: string;
  clientName?: string;
  passwordMessage?: string;
  // Mirrors RegistrationService's own field, which is itself only set once
  // validatePasswordStrength has run.
  passwordStrength?: string;
  confirmPassword?: string;
  currentNumber?: any;
  cellNumber: string = '';
  pwd?: string;
  otpType?: string;
  otpValue: string = '';
  score: number = 0;
  recaptchav3_token?: string;
  termsAndConditions?: boolean;
  registerError?: boolean;
  otpSent?: boolean;
  switchBullet?: boolean;
  hasUpperAndLower: boolean | null = null;
  hasMinLength: boolean | null = null;
  hasNumber: boolean | null = null;
  hasSymbol: boolean | null = null;
  bar0?: string;
  bar1?: string;
  bar2?: string;
  scoreColor?: string;
  defaultErrorMessage: string = 'An error occurred. Please try again';

  otpTypeLabels = [
    { label: "SMS", value: "SMS" },
    { label: "WhatsApp", value: "WhatsApp" }
  ];
  passwordMatch?: boolean;

  get otpTypeLabel(): string {
    return this.otpType === 'SMS' ? 'phone messaging app' : this.otpType === 'WhatsApp' ? 'WhatsApp' : '';
  }

  routingState: { [k: string]: any; };
  passwordDisabled = true;
  resetRequestId?: string;
  otpDisabled = true;
  // Set synchronously in ngOnInit before Angular's first change-detection pass can read it.
  forgotPasswordForm!: FormGroup;
  arePasswordsDirty: boolean = false;
  constructor() {
    const router = this.router;

    const nav = router.currentNavigation() || { extras: { state: {} } };
    const extras = nav.extras || {};
    this.routingState = extras.state || {};
  }

  ngOnInit() {
    this.route.queryParams.subscribe(queryParams => {
      if (queryParams['returnUrl']) {
        this.returnUrl = queryParams['returnUrl'];
      }
      if (queryParams['email']) {
        this.resetRequestId = queryParams['resetRequestId'];
        this.forgotPasswordForm.controls.emailAddress.setValue(queryParams['email']);
        this.returnUrl = queryParams['returnUrl'];
      } else {
        const params = this.routingState;
        if (params) {
          this.returnUrl = params['returnUrl'];
        }
      }
      // BUG (found adopting strict mode, fixed here): none of the branches above are guaranteed to
      // run (no returnUrl in either the query params or routingState), in which case this.returnUrl
      // stays unset and .match() on it threw TypeError, breaking this subscription silently.
      const match = this.returnUrl?.match(/redirect_uri=[^\&]+/);
      if (match && match.length > 0) {
        this.redirectUrl = match[0].replace('auth-callback', '').replace('redirect_uri=', '').replace(/%2F/g, '/').replace(/%3A/g, ':');
      }
      if (queryParams['appID']) {
        this.appID = queryParams['appID'];
        if (this.appID === 'Citadel.Launcher.Msz') {

        }
      }
    });

    this.forgotPasswordForm = this.fb.group({
      emailAddress: ['', [Validators.required, Validators.email]]
    });
    this.setPasswordForm();
  }

  setPasswordForm(): void {
    this.passwordForm = new FormGroup({
      password: new FormControl('', { validators: Validators.required }),
      confirmPassword: new FormControl('', { validators: Validators.required }),
    });
  }

  checkInput() {
    this.passwordMatch = this.editedPasswords.password === this.editedPasswords.confirmPassword;
    this.arePasswordsDirty = this.passwordForm.controls.confirmPassword.dirty || this.passwordForm.controls.password.dirty;
  }

  updateValidity(e: Event) {
    const input = e.target as HTMLInputElement;
    input.value = input.value.replace(/ /g, '');
    this.forgotPasswordForm.controls.emailAddress.patchValue(input.value);
  }

  beforeValidateEmail() {
    this.recaptchaService.execute('validateEmail').subscribe({
      next: (token) => {
        this.recaptchav3_token = token;
        this.validateEmail();
      },
      error: _error => {
        this.notify.error('Error verifying the request (reCaptcha v3)');
      }
    });
  }

  validateEmail() {
    if (!this.forgotPasswordForm.value.emailAddress.match(/^([a-zA-Z0-9_\-\.]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]+)$/)) {
      return;
    }

    this.http.post<string>(`/api/auth/forgotpassword`,
      { email: this.forgotPasswordForm.value.emailAddress, returnUrl: this.returnUrl, recaptchaToken: this.recaptchav3_token },
      { headers: { 'Content-Type': 'application/json' }, withCredentials: true, }).subscribe({
        next: res => {
          if (res.match(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)) {
            this.resetRequestId = res;
            this.currentTemplate = this.resetTemplate();
          } else {
            this.notify.error(res);
          }
        }, error: _error => {
          this.notify.error('An error occurred validating the email address');
          //window.location.href = this.credentials.returnUrl;
        }
      });
  }

  return() {
    window.location.href = this.redirectUrl ?? '';
  }

  updateIdentification(e: Event, id: string): void {
    if (id === 'email') {
      (e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value.replace(/ /g, '');
    }
  }

  updateOtp(e: string): void {
    this.otpValue = e;
  }

  updatePassword(e: Event, password: string): void {
    this.checkPasswordStrength(e, password);
    this.editedPasswords.password = (e.target as HTMLInputElement).value;
    this.checkInput();
  }
  updateConfirmPassword(e: Event): void {
    this.editedPasswords.confirmPassword = (e.target as HTMLInputElement).value;
    this.checkInput();
  }
  resendOTP(): void {
    this.otpSent = false;
    this.currentTemplate = this.otpchannel();
  }
  checkPasswordStrength(e: Event, password: string): void {
    this.registration.validatePasswordStrength(password === 'password' ? (e.target as HTMLInputElement).value : this.pwd ?? '');
    this.passwordStrength = this.registration.passwordStrength;
    this.hasUpperAndLower = this.registration.hasUpperAndLower;
    this.hasMinLength = this.registration.hasMinLength;
    this.hasNumber = this.registration.hasNumber;
    this.hasSymbol = this.registration.hasSymbol;
    this.scoreColor = this.registration.scoreColor;
    this.bar0 = this.registration.bar0;
    this.bar1 = this.registration.bar1;
    this.bar2 = this.registration.bar2;
    this.switchBullet = this.registration.switchBullet;
  }

  goToSendOTP() {

    if (this.passwordMatch) {

      if (this.hasUpperAndLower && this.hasMinLength && this.hasNumber && this.hasSymbol) {
        this.selectedIndex = 1;
        this.currentTemplate = this.otpchannel();
      } else {
        this.notify.error("Please choose a strong password by following our guidelines on the screen.");
      }
    } else {
      this.notify.error("Passwords do not match.");
    }
  }

  goToEnterOTP(): void {
    if (this.otpType) {
      this.sendOtp();
    } else {
      this.notify.error('Please select an option before you continue');
    }
  }
  resetPassword() {
    if (!this.otpValue || this.otpValue.length < 4) {
      this.notify.error('A 4-digit OTP is required');
    } else {
      this.redirectUrl = this.signInService.signInReturnUrl;
      this.selectedIndex = 1;
      this.http.post<string>(`/api/auth/resetpassword`,
        { email: this.signInService.passwordResetEmail, password: this.passwordForm.value.password, otp: parseInt(this.otpValue), resetRequestId: this.signInService.passwordResetRequestId, returnUrl: this.signInService.signInReturnUrl },
        { headers: { 'Content-Type': 'application/json' }, withCredentials: true, }).subscribe({
          next: res => {
            if (res) {
              this.notify.error(res);
            } else {
              this.notify.success('Please sign in again using your new password.', "Your password has been reset.");
              this.router.navigateByUrl('/sign-in', { state: { returnUrl: this.signInService.signInReturnUrl } });
            }
          }, error: _ => {
            this.notify.error(this.defaultErrorMessage);
            this.otpSent = false;
            this.currentTemplate = this.password();
            this.selectedIndex = 0;
          }
        });
    }
  }

  sendOtp(): void {
    if (!this.otpSent) {
      this.otpSent = true;
      if (this.otpType) {
        this.registration.sendResetOtp(this.otpType, this.signInService.passwordResetRequestId ?? '').subscribe({
          next: _ => {
            this.notify.success('OTP sent');
            this.currentTemplate = this.enterotp();
          }, error: error => {
            this.notify.error(error.message);
            this.currentTemplate = this.enterotp();
          }
        });
      } else {
        this.notify.error('Please select an option before you continue');
      }
    }
  }

  handleKeyUp(e: KeyboardEvent, step: string) {
    if (e.keyCode === 13) {
      switch (step) {
        case "goToEnterOTP":
          this.goToEnterOTP();
          break;
        case "resetPassword":
          this.resetPassword();
          break;
      }
    }
  }

}
